import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { StartFreeButton } from '@/components/marketing/free-plan-cta'

const URL = 'https://uptrack.app/blog/what-is-mtbf-meaning-formula'
const DESCRIPTION =
  'MTBF stands for mean time between failures. The formula with a worked example, how it differs from MTTR and MTTF, and how to measure it for a website.'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What does MTBF stand for?',
    a: 'MTBF stands for mean time between failures. It is the average time a repairable system runs between one failure and the next.',
  },
  {
    q: 'How do you define MTBF?',
    a: 'MTBF is the total operating time of a repairable system divided by the number of failures in that time. A system that ran 1,000 hours with 10 failures has an MTBF of 100 hours.',
  },
  {
    q: 'What is the difference between MTBF and MTTF?',
    a: 'MTBF is for repairable systems: it averages the running time between failures, and the system is fixed and runs again. MTTF, mean time to failure, is for items that are replaced, not repaired, such as a disk or a light bulb.',
  },
  {
    q: 'What is the MTBF full form and the MTTR full form?',
    a: 'MTBF is mean time between failures. MTTR is mean time to repair, and some teams read the R as recover, respond or resolve, so write down which one you mean.',
  },
  {
    q: 'What is a good MTBF?',
    a: 'There is no universal number. Compare your MTBF with your own past months and with the target you promised. Judge it together with MTTR, because a long MTBF with a long MTTR can still miss an uptime target.',
  },
  {
    q: 'Does a high MTBF mean a unit will last that long?',
    a: 'No. MTBF is an average rate over many units or many failures. Under the usual constant-failure-rate assumption, only about 37% of units are still running after one MTBF has passed.',
  },
]

export const Route = createFileRoute('/_site/blog/what-is-mtbf-meaning-formula')({
  component: WhatIsMtbfPage,
  head: () => ({
    meta: [
      { title: 'What Is MTBF? Meaning, Formula, MTBF vs MTTR — Uptrack' },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: 'What Is MTBF? Meaning, Formula, MTBF vs MTTR vs MTTF' },
      {
        property: 'og:description',
        content:
          'MTBF stands for mean time between failures. Formula, a worked example, MTBF vs MTTR vs MTTF, and how to measure it from monitoring data.',
      },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-what-is-mtbf-meaning-formula.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: URL },
      { property: 'article:published_time', content: '2026-10-13' },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'What Is MTBF? Meaning, Formula and MTBF vs MTTR vs MTTF',
          description: DESCRIPTION,
          datePublished: '2026-10-13',
          dateModified: '2026-10-13',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app' },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const metrics = [
  {
    name: 'MTBF',
    stands: 'Mean time between failures',
    measures: 'Average running time between failures of a repairable system',
    use: 'How often something breaks',
  },
  {
    name: 'MTTR',
    stands: 'Mean time to repair (or recover, respond, resolve)',
    measures: 'Average time one failure lasts, from start to fixed',
    use: 'How fast you fix it',
  },
  {
    name: 'MTTF',
    stands: 'Mean time to failure',
    measures: 'Average life of an item that is replaced, not repaired',
    use: 'Planning hardware replacement',
  },
  {
    name: 'MTTD',
    stands: 'Mean time to detect',
    measures: 'Average time from the start of a failure until someone knows',
    use: 'How good your monitoring is',
  },
]

function WhatIsMtbfPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 13, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">
        What is MTBF? Meaning, formula and MTBF vs MTTR vs MTTF
      </h1>

      <section className="mb-12">
        <p className="mb-4 text-lg leading-[1.7] text-body">
          MTBF stands for <strong>mean time between failures</strong>. It is the average time a repairable system runs
          between one failure and the next. A higher MTBF means failures are rarer. You calculate it by dividing the total
          operating time by the number of failures.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          The term comes from hardware reliability, where it describes servers, disks and power supplies. Teams that run
          websites and APIs use the same idea: how many hours does the service stay up between outages? This guide gives the
          meaning, the formula with the arithmetic shown, the difference between MTBF, MTTR, MTTF and MTTD, and a way to
          measure MTBF from real monitoring data without fooling yourself.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">MTBF meaning in plain words</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          MTBF answers one question: when this thing breaks, then gets fixed, how long does it usually run before it breaks
          again? A service with an MTBF of 700 hours fails about once a month. A service with an MTBF of 7 hours is in
          trouble.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Three points help you read the number correctly:
        </p>
        <ul className="mb-4 list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>It is for repairable systems.</strong> The system fails, you repair it, it runs again. For an item you
            throw away after one failure, the right metric is MTTF.
          </li>
          <li>
            <strong>It is an average.</strong> An MTBF of 100 hours does not mean a failure every 100 hours. Failures can
            come in bursts, for example after a bad deploy.
          </li>
          <li>
            <strong>It needs a clear definition of failure.</strong> A crash, a timeout, an HTTP 500 for more than a minute
            and a slow page are different failures. Decide first, then count.
          </li>
        </ul>
        <p className="text-lg leading-[1.7] text-body">
          Two conventions exist. Most reliability references, and incident-management guides such as Atlassian's, divide
          operating time by the number of failures, so repair time is not part of the number. Some references measure from
          the start of one failure to the start of the next, which adds the repair time (MTBF = MTTF + MTTR, the form on our{' '}
          <Link to="/glossary/mtbf" className="underline">
            MTBF glossary page
          </Link>
          ). When repairs are short compared with the time between failures, the two give almost the same result. This guide
          uses the first convention, because the availability formula below needs it.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">The MTBF formula, with a worked example</h2>
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <p className="mb-2 text-xs font-medium text-muted-foreground">MTBF formula</p>
          <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
            <code className="text-muted-foreground">{`MTBF = total operating time / number of failures

Example: one API over 90 days
  Window:          90 days x 24 h = 2,160 hours
  Incident 1:      30 minutes
  Incident 2:      90 minutes
  Incident 3:      60 minutes
  Total downtime:  180 minutes = 3 hours
  Operating time:  2,160 - 3 = 2,157 hours
  Failures:        3

  MTBF = 2,157 / 3 = 719 hours (about 30 days)
  MTTR = 3 hours / 3 incidents = 1 hour`}</code>
          </pre>
        </div>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Counted from one failure start to the next instead, the same data gives 720 hours, because 2,160 hours divided by 3
          failures is 720. The difference is exactly one MTTR, and that is why the two conventions agree when repairs are
          short.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          The inputs are plain: a time window, and the start and end time of every outage. If you do not have those
          timestamps, you cannot compute MTBF. You can only guess it.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">MTBF vs MTTR vs MTTF vs MTTD</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          The four metrics look alike and answer four different questions. MTBF is about frequency. MTTR is about duration.
          MTTF is about the life of something you replace. MTTD is about how long a failure goes unnoticed.
        </p>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Metric</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Stands for</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">Measures</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">Use it for</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.name} className="border-b align-top last:border-0">
                  <td className="py-3 pr-4 font-medium">{m.name}</td>
                  <td className="py-3 px-3 text-muted-foreground">{m.stands}</td>
                  <td className="py-3 px-3 text-muted-foreground">{m.measures}</td>
                  <td className="py-3 pl-3 text-muted-foreground">{m.use}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          <strong>MTBF vs MTTF</strong> in one sentence: MTBF is for systems you repair, MTTF is for items you replace. A web
          service is repairable, so MTBF fits. A hard disk that you swap when it dies is an MTTF case. More detail on each
          term: <Link to="/glossary/mttr" className="underline">MTTR</Link>,{' '}
          <Link to="/glossary/mttf" className="underline">MTTF</Link> and{' '}
          <Link to="/glossary/mttd" className="underline">MTTD</Link>.
        </p>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          <strong>MTBF and MTTR</strong> work as a pair. MTBF tells you how often you get hurt, MTTR tells you how long each
          injury lasts. Improving either one raises your uptime.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">MTBF, availability and your uptime percent</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          In steady state, availability is the share of time a system is up, and it follows from the two numbers above:
        </p>
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Availability formula</p>
          <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
            <code className="text-muted-foreground">{`Availability = MTBF / (MTBF + MTTR)

Same API:
  719 / (719 + 1) = 719 / 720 = 99.861%
  Check:  2,157 up hours / 2,160 total hours = 99.861%

A 99.9% target allows 2,160 x 0.001 = 2.16 hours of downtime in 90 days.
We had 3 hours, so we missed the target.

Halve every repair (15, 45 and 30 minutes = 1.5 hours of downtime):
  MTBF = 2,158.5 / 3 = 719.5 hours,  MTTR = 0.5 hours
  719.5 / (719.5 + 0.5) = 99.931%   -> target met`}</code>
          </pre>
        </div>
        <p className="text-lg leading-[1.7] text-body">
          The last block shows why MTTR matters as much as MTBF. The same three incidents, repaired twice as fast, turned a
          missed 99.9% target into a met one, with no failure removed. The formula assumes failures and repairs are random and
          that the time you count as downtime is only unplanned repair. To see what a given percentage means in minutes, use
          the <Link to="/tools/uptime-calculator" className="underline">uptime calculator</Link> or the{' '}
          <Link to="/tools/sla-calculator" className="underline">SLA calculator</Link>, and read{' '}
          <Link to="/blog/what-is-uptime-sla" className="underline">what a good uptime SLA is</Link>.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">How to measure MTBF for a website or API</h2>
        <ol className="mb-4 list-decimal space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>Pick a window.</strong> 90 days is a common start. Fewer than about ten failures gives a number that
            swings wildly, so widen the window or say that the sample is small.
          </li>
          <li>
            <strong>Define a failure.</strong> For example: the endpoint fails for two or more consecutive checks. A
            single blip is not an incident.
          </li>
          <li>
            <strong>Take the start and end times from a monitor, not from memory.</strong> People round outages to
            "about an hour" and forget the short ones.
          </li>
          <li>
            <strong>Decide how to treat planned maintenance.</strong> Most SLAs exclude it. Exclude it from both the failure
            count and the downtime, and report it separately. Be consistent from month to month.
          </li>
          <li>
            <strong>Compute it.</strong> Operating hours divided by failures, as in the example. If there were no failures
            in the window, MTBF is not infinite. It is at least the length of the window.
          </li>
        </ol>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Two things distort the result. <strong>False alarms</strong> count as failures, so every false incident drags MTBF
          down. Confirming a failure before it opens an incident, for example after consecutive failed checks, removes most
          of that noise. <strong>The check interval</strong> sets your precision: with 5-minute checks a short outage can be
          missed, and every start and end time is off by up to 5 minutes, which also moves MTTR. Shorter intervals give truer
          numbers.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Common mistakes</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>Reading MTBF as a lifespan.</strong> A hardware vendor's MTBF of 100,000 hours describes a failure rate
            across many units. It does not promise one unit lasts 11 years.
          </li>
          <li>
            <strong>Averaging different services.</strong> A rock-solid billing API and a flaky internal tool in one average
            hide both stories. Compute MTBF per service.
          </li>
          <li>
            <strong>Counting every alert as a failure.</strong> Alerts and failures are not the same. Count confirmed
            incidents.
          </li>
          <li>
            <strong>Ignoring the trend.</strong> Releases change the failure rate. One number for a whole year hides a bad
            month. Plot it per month.
          </li>
          <li>
            <strong>Mixing the two conventions.</strong> Operating time per failure and start-to-start time differ by one
            MTTR. Pick one and write it down.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">When MTBF is not the right metric</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>You have very few failures.</strong> With two incidents a year, MTBF is a guess. Track downtime minutes
            and your <Link to="/glossary/error-budget" className="underline">error budget</Link> instead.
          </li>
          <li>
            <strong>Severity differs a lot.</strong> A 30-second blip and a 6-hour outage each count as one failure. Customer
            impact, such as downtime minutes, tells you more.
          </li>
          <li>
            <strong>You replace, not repair.</strong> Use MTTF for disks, batteries and similar parts.
          </li>
          <li>
            <strong>Your customers feel duration.</strong> If one long outage hurts more than many short ones, watch MTTR.
          </li>
        </ul>
        <p className="mt-4 text-lg leading-[1.7] text-muted-foreground">
          MTBF is a good summary for a dashboard and a poor tool for a single decision. Treat Atlassian's own warning the same
          way: these metrics are a baseline for better questions, not an answer.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Where monitoring data fits in</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          MTBF needs exact start and end times of outages. An uptime monitor records them. Uptrack checks every 30 seconds on
          every plan and opens an incident only after consecutive failed checks, so the incident list has clean start and
          resolve times, with few false alarms. One honest limit: <strong>Uptrack does not compute MTBF or show an MTBF figure
          today.</strong> You take the incident start and resolve times from the incident history and do the arithmetic above.
          See <Link to="/features/uptime-monitoring" className="underline">uptime monitoring</Link> for how checks and
          confirmation work.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-6 text-2xl font-semibold">MTBF questions</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Get the timestamps MTBF needs</h2>
        <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
          30-second checks, incidents with exact start and resolve times. Paid plans from $3/mo, with a 30-day money-back
          guarantee.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" asChild>
            <Link to="/pricing">
              See plans
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  )
}
